/**
 * AdvancedLearnInDrawer — the Nurse Call tab's add-device flow.
 *
 * Replaces the category → type → model → behavior cascade in AddDeviceDrawer with
 * a single pick from a catalog that already knows those answers
 * (`sp_getAdvLearnInDevices`). Behavior and model are only ever asked about when
 * the picked device genuinely has more than one.
 *
 * **Phases 1-3 of docs/ADVANCED_LEARN_IN_PLAN.md.** The picker and the assignment
 * targets are in: a device is added live against an area or a resident, or
 * explicitly left unassigned (which reproduces exactly what the old drawer always
 * wrote). What you assign *to* follows the base toggle — an area device to an
 * area, a resident device to a resident — so there are two target choices, not
 * three.
 *
 * Still to come (phase 4): a serial already in the system is currently refused;
 * it should instead offer to move that device to the chosen target.
 *
 * AddDeviceDrawer is intentionally left in place: the System Devices tab still
 * mounts it with different category rules.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchAdvLearnInDevices, publishLearnIn, pollLearnInDevices } from '../api/devicesApi.js';
/* Same queries the device profile uses for its assignment dropdown, so the two
   surfaces offer an identical list of areas and residents. */
import { fetchAreas, fetchResidents } from '../device-profile/api/deviceProfileApi.js';
/* SearchableSelect, not the v1 SearchableDropdown: this is the V2-styled picker
   the Add Resident drawer uses for its area field
   (residents/components/ResidentDrawer.jsx:241-251), so the two match. */
import SearchableSelect from '../../../components/v2/SearchableSelect.jsx';
import Drawer from '../../../components/v2/Drawer.jsx';
import {
  foldCatalog, catalogForBase, splitTiles, TILE_COUNT,
  needsUseChoice, needsModelChoice, resolveUseId, resolveModel, canListen,
  normalizeSerial, validateSerial, findExistingDevice,
  areaOptions, residentOptions,
  describeAssignment, isAlreadyOnTarget, isSameDeviceType,
  INOVONICS_ID,
} from './advLearnIn.js';
import '../../../components/v2/easy-learn-in.css';

const LISTEN_SECONDS = 60;

/** Device image, or a glyph. Many catalog entries have no image, and several share one. */
const DeviceImage = ({ row, size }) => (
  row?.image_url ? (
    <img
      src={`/static/image/${row.image_url}`}
      alt=""
      aria-hidden="true"
      style={{ width: size, height: size, objectFit: 'contain' }}
      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
    />
  ) : (
    <span
      aria-hidden="true"
      style={{
        width: size, height: size, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)',
      }}
    >
      <i className="fe fe-cpu" style={{ fontSize: size * 0.5 }} />
    </span>
  )
);

const AdvancedLearnInDrawer = ({
  isOpen,
  onClose,
  onSave,
  onBatchComplete,
  existingDevices = [],
  onAssignExisting,
  campusId,
  sessionId,
  roleWeight = 0,
  initialBase,
  title = 'Add Device',
}) => {
  const [base, setBase] = useState(initialBase === 'area' ? 'area' : 'resident');
  const [step, setStep] = useState('device');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [picked, setPicked] = useState(null);
  const [useId, setUseId] = useState('');
  const [modelId, setModelId] = useState('');
  const [search, setSearch] = useState('');
  const [description, setDescription] = useState('');

  /* Target. `mode` is 'assign' or 'unassigned'; what you assign *to* follows the
     base rather than being a free choice — an area device belongs to an area, a
     resident device to a resident. Same rule the device profile's own assignment
     drawer uses (device-profile/components/BasicInfoDrawer.jsx), and
     what "populate resident_id or area_id depending the device add selection"
     describes. So there are two target options, not three. */
  const [targetMode, setTargetMode] = useState('assign');
  /* String, because SearchableSelect option values are strings. Converted at the
     payload boundary. */
  const [targetId, setTargetId] = useState('');
  const [areas, setAreas] = useState([]);
  const [residents, setResidents] = useState([]);

  const [serial, setSerial] = useState('');
  const [serialError, setSerialError] = useState('');

  /* Multi is area-only (plan §9-C): a dozen pull cords in one room is the real
     use case; a dozen pendants on one resident is not. */
  const [multiMode, setMultiMode] = useState(false);
  /* [{ serial, descriptions }] — each device in a batch gets its own location,
     because a dozen cords going into one area are still in twelve different
     places, and that text is how staff tell them apart on an alert. */
  const [serials, setSerials] = useState([]);
  const [newSerial, setNewSerial] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchFailures, setBatchFailures] = useState([]);

  const [listening, setListening] = useState(false);
  const [listenCount, setListenCount] = useState(0);
  const [heard, setHeard] = useState([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');


  const isAreaBase = base === 'area';

  // ---- reset ---------------------------------------------------------------
  const resetPick = useCallback(() => {
    setPicked(null);
    setStep('device');
    setUseId('');
    setModelId('');
    setSerial('');
    setSerialError('');
    setDescription('');
    setSerials([]);
    setNewSerial('');
    setNewDesc('');
    setBatchFailures([]);
    setBatchProgress({ current: 0, total: 0 });
    setListening(false);
    setListenCount(0);
    setHeard([]);
    setError('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setBase(initialBase === 'area' ? 'area' : 'resident');
    setSearch('');
    setMultiMode(false);
    setTargetMode('assign');
    setTargetId('');
    resetPick();
  }, [isOpen, initialBase, resetPick]);

  // ---- assignment targets --------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    Promise.all([fetchAreas(campusId), fetchResidents(campusId)])
      .then(([a, r]) => { setAreas(a || []); setResidents(r || []); })
      .catch(() => { setAreas([]); setResidents([]); });
  }, [isOpen, campusId]);

  // ---- catalog -------------------------------------------------------------
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setLoadError('');
    fetchAdvLearnInDevices()
      .then((data) => setRows(data || []))
      .catch(() => setLoadError('Could not load the device list.'))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const catalog = useMemo(() => foldCatalog(rows), [rows]);

  const forBase = useMemo(
    () => catalogForBase(catalog, base, roleWeight),
    [catalog, base, roleWeight],
  );

  /* While searching, one flat result list reads better than tiles plus a list —
     the user already knows what they're looking for. */
  const searchTerm = search.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!searchTerm) return null;
    return forBase.filter((e) => (
      (e.device_type_name || '').toLowerCase().includes(searchTerm)
      || (e.device_category_short_name || '').toLowerCase().includes(searchTerm)
    ));
  }, [forBase, searchTerm]);

  const { tiles, rest } = useMemo(() => splitTiles(forBase, TILE_COUNT), [forBase]);

  // ---- resolved behavior / model ------------------------------------------
  const model = useMemo(() => resolveModel(picked, modelId), [picked, modelId]);
  const resolvedUseId = useMemo(() => resolveUseId(picked, useId), [picked, useId]);
  const askUse = needsUseChoice(picked);
  const askModel = needsModelChoice(picked);
  const listenable = canListen(picked, model) && Boolean(sessionId);

  // ---- listen --------------------------------------------------------------
  useEffect(() => {
    if (!listening) return undefined;
    const interval = setInterval(() => {
      setListenCount((prev) => {
        const next = prev + 1;
        if (next >= LISTEN_SECONDS) {
          setListening(false);
          return LISTEN_SECONDS;
        }
        if (next % 4 === 0) {
          pollLearnInDevices({ campusId, clientId: sessionId })
            .then((data) => setHeard(data || []))
            .catch(() => {});
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [listening, campusId, sessionId]);

  const startListen = useCallback(() => {
    if (!listenable) return;
    setHeard([]);
    setListenCount(0);
    setListening(true);
    /* The exact product code — we know precisely which device was picked, which is
       the entire point of choosing it before listening. */
    publishLearnIn({
      campusId,
      clientId: sessionId,
      productType: [model.product_type_id],
      msgClass: model.message_class,
    }).then(([ok]) => {
      if (!ok) { setListening(false); setError('Could not start listening.'); }
    });
  }, [listenable, campusId, sessionId, model]);

  // Escape stops an active listen first, so it can't run on after close; the Drawer
  // shell closes the drawer.
  useEffect(() => {
    if (!isOpen || !listening) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setListening(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, listening]);

  // ---- serials -------------------------------------------------------------
  const handlePick = useCallback((entry) => {
    setPicked(entry);
    setStep('configure');
    setError('');
    setSerialError('');
    /* Pre-select when there's exactly one, so the resolved value is visible in
       the summary even though no control is rendered for it. */
    setUseId(entry.uses.length === 1 ? String(entry.uses[0].device_use_id) : '');
    setModelId(entry.models.length === 1 ? String(entry.models[0].model_id) : '');
  }, []);

  const duplicate = useMemo(
    () => findExistingDevice(existingDevices, serial, picked),
    [existingDevices, serial, picked],
  );

  const addSerialToBatch = useCallback(() => {
    const err = validateSerial(newSerial, picked);
    if (err) { setSerialError(err); return; }
    const clean = normalizeSerial(newSerial, picked);
    if (serials.some((x) => x.serial === clean)) {
      setSerialError('That serial is already in the list.');
      return;
    }
    /* Batch stays refuse-only. Reassigning is a per-device decision with its own
       confirmation, and burying twelve of those inside one bulk add would be a
       way to move eleven devices by accident. */
    if (findExistingDevice(existingDevices, clean, picked)) {
      setSerialError('That serial is already in the system — add it on its own to reassign it.');
      return;
    }
    setSerials((prev) => [...prev, { serial: clean, descriptions: newDesc.trim() }]);
    setNewSerial('');
    setNewDesc('');
    setSerialError('');
  }, [newSerial, newDesc, picked, serials, existingDevices]);

  const removeSerial = useCallback((value) => {
    setSerials((prev) => prev.filter((item) => item.serial !== value));
  }, []);

  // ---- submit --------------------------------------------------------------
  const assigning = targetMode === 'assign';

  /* Sorted in advLearnIn.js: areas by name, residents by first name — both
     case-insensitive and numeric-aware so "Room 2" precedes "Room 10". */
  const targetOptions = useMemo(
    () => (isAreaBase ? areaOptions(areas) : residentOptions(residents)),
    [isAreaBase, areas, residents],
  );

  /** The chosen area or resident row, whichever the base calls for. */
  const targetRow = useMemo(() => {
    if (!assigning || !targetId) return null;
    const list = isAreaBase ? areas : residents;
    const idKey = isAreaBase ? 'area_id' : 'resident_id';
    return (list || []).find((x) => String(x[idKey]) === String(targetId)) || null;
  }, [assigning, targetId, isAreaBase, areas, residents]);

  const buildFormData = useCallback((finalSerial, descOverride) => {
    const areaTarget = assigning && isAreaBase;
    const residentTarget = assigning && !isAreaBase;

    return {
      campus_id: campusId,
      manufacture_id: picked.manufacture_id,
      manufacture_uuid: finalSerial,
      device_name: picked.device_type_name || '',
      device_use_id: resolvedUseId,
      manufacture_model_id: model?.model_id ? Number(model.model_id) : 0,
      device_type_id: picked.device_type_id,

      /* Assigned devices go in live; "leave unassigned" reproduces exactly what
         the old drawer always wrote, which is why it stays an explicit choice
         rather than being dropped (plan §6). */
      is_active: assigning ? 1 : 0,
      area_id: areaTarget ? Number(targetId) : 0,
      resident_id: residentTarget ? Number(targetId) : 0,

      /* The vendor packagers on the resident path read this to know which area
         the resident sits in (mysql_transaction.py:3724 copies it over
         `area_id`). See the note below on why it can be 0 today. */
      resident_area_id: residentTarget ? Number(targetRow?.area_id || 0) : 0,

      /* area_name only on the area path — the packagers there expect it and the
         resident path has never sent it. */
      ...(areaTarget ? { area_name: targetRow?.area_name || '' } : {}),

      is_resident: !isAreaBase,
      is_area_device: isAreaBase ? 1 : 0,
      is_alerting_now: false,
      is_server: false,
      escalation_enabled: true,
      /* Batch entries carry their own location; single adds use the shared
         field. Resident devices never send placement text. */
      descriptions: isAreaBase ? String(descOverride ?? description).trim() : '',
    };
  }, [campusId, picked, resolvedUseId, model, isAreaBase, description, assigning, targetId, targetRow]);

  const transType = isAreaBase ? 'device_add_area' : 'device_add_resident';

  /* Must sit AFTER targetRow: handleReassign closes over it, and a useCallback
     dependency array is evaluated during render, so declaring this above the
     `const targetRow` threw a temporal-dead-zone ReferenceError and crashed the
     drawer on open. */
  /* How a duplicate resolves depends on the target, so all three questions get
     answered up front (plan §7). */
  const dupWhere = useMemo(() => describeAssignment(duplicate), [duplicate]);
  const dupOnTarget = useMemo(
    () => isAlreadyOnTarget(duplicate, base, targetId),
    [duplicate, base, targetId],
  );
  const dupTypeMatches = useMemo(
    () => isSameDeviceType(duplicate, picked),
    [duplicate, picked],
  );
  /* Reassignable when there's a target to move it to, it isn't already there,
     and the caller gave us a way to do it. */
  const canReassign = Boolean(duplicate) && assigning && !dupOnTarget
    && typeof onAssignExisting === 'function';

  const handleReassign = useCallback(async () => {
    if (!canReassign) return;
    setSaving(true);
    setError('');
    const result = await onAssignExisting(duplicate, {
      base,
      targetId: Number(targetId),
      targetRow,
      descriptions: isAreaBase ? description.trim() : '',
    });
    setSaving(false);
    const ok = Array.isArray(result) ? result[0] : Boolean(result);
    if (ok) onClose();
    else setError((Array.isArray(result) ? result[1] : '') || 'Could not reassign that device.');
  }, [canReassign, onAssignExisting, duplicate, base, targetId, targetRow, isAreaBase, description, onClose]);

  /* Choosing "assign" without picking anything is the one new way to leave the
     form incomplete, so it gates submit alongside behavior and model. */
  const targetReady = !assigning || String(targetId) !== '';

  const singleReady = Boolean(picked)
    && !validateSerial(serial, picked)
    && !duplicate
    && targetReady
    && (!askUse || useId !== '')
    && (!askModel || modelId !== '');

  const batchReady = Boolean(picked)
    && serials.length > 0
    && targetReady
    && (!askUse || useId !== '')
    && (!askModel || modelId !== '');

  const canSubmit = (multiMode ? batchReady : singleReady) && !saving;

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');

    if (multiMode) {
      setBatchFailures([]);
      const total = serials.length;
      const failures = [];
      setSaving(true);
      for (let i = 0; i < total; i += 1) {
        setBatchProgress({ current: i + 1, total });
        // eslint-disable-next-line no-await-in-loop
        const entry = serials[i];
        const result = await onSave(
          buildFormData(entry.serial, entry.descriptions), transType, { silent: true },
        );
        const ok = Array.isArray(result) ? result[0] : Boolean(result);
        if (!ok) {
          failures.push({ ...entry, error: (Array.isArray(result) ? result[1] : '') || 'Failed to add device' });
        }
      }
      setSaving(false);
      setBatchProgress({ current: 0, total: 0 });
      if (typeof onBatchComplete === 'function') {
        onBatchComplete({ added: total - failures.length, failed: failures.length, total });
      }
      if (failures.length === 0) {
        onClose();
      } else {
        /* Keep only what failed, so the retry is one click and the user can see
           which serials to check. */
        setBatchFailures(failures);
        /* Keep the failures — with their locations — so a retry is one click. */
        setSerials(failures.map((f) => ({ serial: f.serial, descriptions: f.descriptions })));
      }
      return;
    }

    setSaving(true);
    const result = await onSave(buildFormData(normalizeSerial(serial, picked)), transType);
    setSaving(false);
    const ok = Array.isArray(result) ? result[0] : Boolean(result);
    if (!ok) setError((Array.isArray(result) ? result[1] : '') || 'Could not add that device.');
  }, [canSubmit, multiMode, serials, onSave, buildFormData, transType, onBatchComplete, onClose, serial, picked]);

  // ---- render --------------------------------------------------------------
  const p = 'dp';

  const renderPickStep = () => {
    if (loading) return <p className="eld-hint">Loading devices…</p>;
    if (loadError) return <p className="eld-error">{loadError}</p>;
    if (forBase.length === 0) {
      return (
        <p className="eld-hint">
          No {isAreaBase ? 'area' : 'resident'} devices are available for your role.
        </p>
      );
    }

    return (
      <>
        <div className={`${p}-drawer__field`}>
          <input
            type="text"
            className={`${p}-drawer__input`}
            placeholder="Search devices…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search devices"
          />
        </div>

        {matches ? (
          matches.length === 0 ? (
            <p className="eld-hint">No devices match that search.</p>
          ) : (
            <div className="eld-list">
              {matches.map((e) => (
                <button key={e.key} type="button" className="eld-row" onClick={() => handlePick(e)}>
                  <DeviceImage row={e} size={24} />
                  <span className="eld-row__name">{e.device_type_name}</span>
                  {e.device_category_short_name ? (
                    <span className="eld-row__meta">{e.device_category_short_name}</span>
                  ) : null}
                </button>
              ))}
            </div>
          )
        ) : (
          <>
            <div className="eld-tiles">
              {tiles.map((e) => (
                <button key={e.key} type="button" className="eld-tile" onClick={() => handlePick(e)}>
                  <DeviceImage row={e} size={56} />
                  <span className="eld-tile__name">{e.device_type_name}</span>
                  {e.device_category_short_name ? (
                    <span className="eld-tile__meta">{e.device_category_short_name}</span>
                  ) : null}
                </button>
              ))}
            </div>

            {rest.length > 0 ? (
              <div style={{ marginTop: 'var(--space-3)' }}>
                <p className={`${p}-drawer__label`}>Other devices</p>
                <div className="eld-list">
                  {rest.map((e) => (
                    <button key={e.key} type="button" className="eld-row" onClick={() => handlePick(e)}>
                      <DeviceImage row={e} size={24} />
                      <span className="eld-row__name">{e.device_type_name}</span>
                      {e.device_category_short_name ? (
                        <span className="eld-row__meta">{e.device_category_short_name}</span>
                      ) : null}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}
      </>
    );
  };

  const renderConfigureStep = () => (
    <>
      {/* Picked device. The manufacturer sits under the name as a quiet
          confirmation — useful when two manufacturers offer a similarly-named
          device, but never something the user has to act on. */}
      <div className="eld-picked">
        <DeviceImage row={picked} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span className="eld-row__name">{picked.device_type_name}</span>
          {picked.manufacture_name ? (
            <span className="eld-picked__meta">{picked.manufacture_name}</span>
          ) : null}
        </div>
        <button type="button" className="eld-link-btn" onClick={resetPick}>Change</button>
      </div>

      {/* ---- Model & behavior --------------------------------------------
          Only rendered when there is genuinely a choice. A device with one of
          each skips this card entirely, which is the point of the redesign. */}
      {askModel || askUse ? (
        <div className="eld-section">
          <p className="eld-section__title">Model &amp; behavior</p>

          {askModel ? (
            <div className={`${p}-drawer__field`}>
              <label className={`${p}-drawer__label`} htmlFor="adv-model">
                Model <span className={`${p}-drawer__required`}>*</span>
              </label>
              <select
                id="adv-model"
                className={`${p}-drawer__select`}
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
              >
                <option value="">Select model…</option>
                {picked.models.map((m) => (
                  <option key={m.model_id} value={m.model_id}>{m.model_name || `Model ${m.model_id}`}</option>
                ))}
              </select>
            </div>
          ) : null}

          {/* Radio rows rather than a select, matching the original Add Device
              drawer (AddDeviceDrawer.jsx:1044-1071): behavior labels are whole
              phrases, and a collapsed dropdown hides the alternatives at exactly
              the moment the user is trying to compare them. */}
          {askUse ? (
            <div className={`${p}-drawer__field`}>
              <label className={`${p}-drawer__label`}>
                Alarm Behavior <span className={`${p}-drawer__required`}>*</span>
              </label>
              <div className={`${p}-drawer__radio-group`} role="radiogroup" aria-label="Alarm Behavior">
                {picked.uses.map((u) => {
                  const selected = String(useId) === String(u.device_use_id);
                  return (
                    <label
                      key={u.device_use_id}
                      className={`${p}-drawer__radio-option${selected ? ' is-selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="adv-alarm-behavior"
                        value={u.device_use_id}
                        checked={selected}
                        onChange={(e) => setUseId(e.target.value)}
                        className={`${p}-drawer__radio-input`}
                      />
                      <span className={`${p}-drawer__radio-text`}>
                        {u.option_text || `Option ${u.option_number}`}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ---- Assignment ---------------------------------------------------
          Above the serial on purpose (plan §4): when a serial turns out to be a
          duplicate, the target is already known, so reassigning is one click. */}
      <div className="eld-section">
        <p className="eld-section__title">Assignment</p>

        <div className={`${p}-drawer__field`}>
          <label className={`${p}-drawer__label ${p}-drawer__label--inline`}>
            <input
              type="checkbox"
              checked={!assigning}
              onChange={(e) => {
                setTargetMode(e.target.checked ? 'unassigned' : 'assign');
                setTargetId('');
              }}
            />
            {' '}Leave unassigned
          </label>
        </div>

        {assigning ? (
          <div className={`${p}-drawer__field`}>
            <label className={`${p}-drawer__label`} htmlFor="adv-target">
              {isAreaBase ? 'Area' : 'Resident'} <span className={`${p}-drawer__required`}>*</span>
            </label>
            <SearchableSelect
              id="adv-target"
              options={targetOptions}
              value={targetId}
              onChange={(val) => setTargetId(val || '')}
              placeholder={isAreaBase ? 'Search or select area...' : 'Search or select resident...'}
              ariaLabel={isAreaBase ? 'Area' : 'Resident'}
              required
              isClearable
            />
          </div>
        ) : (
          <p className="eld-hint">
            The device is added but stays inactive until someone assigns it.
          </p>
        )}
      </div>

      {/* ---- Identify ------------------------------------------------------ */}
      <div className="eld-section">
        <p className="eld-section__title">
          {multiMode ? 'Serial numbers & locations' : 'Serial number'}
        </p>

        {/* Batch is area-only. Switching base clears it (see the base handler). */}
        {isAreaBase ? (
          <div className={`${p}-drawer__field`}>
            <label className={`${p}-drawer__label ${p}-drawer__label--inline`}>
              <input
                type="checkbox"
                checked={multiMode}
                onChange={(e) => {
                  setMultiMode(e.target.checked);
                  setSerial('');
                  setSerials([]);
                  setNewSerial('');
                  setNewDesc('');
                  setSerialError('');
                }}
              />
              {' '}Add several of this device
            </label>
          </div>
        ) : null}

        {multiMode ? (
          <div className={`${p}-drawer__field`}>
            {/* Serial and its location are entered as a pair: a dozen cords in
                one area are still in twelve different places, and that text is
                how staff know which one fired. */}
            <div className="eld-batch-entry">
              <input
                type="text"
                aria-label="Serial number"
                className={`${p}-drawer__input ${serialError ? 'is-invalid' : ''}`}
                value={newSerial}
                onChange={(e) => { setNewSerial(e.target.value); setSerialError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSerialToBatch(); } }}
                placeholder="Serial number"
              />
              <input
                type="text"
                aria-label="Location within area"
                className={`${p}-drawer__input`}
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSerialToBatch(); } }}
                placeholder="e.g Bathroom (optional)"
              />
              <button
                type="button"
                className={`${p}-btn ${p}-btn--secondary eld-batch-entry__add`}
                onClick={addSerialToBatch}
              >
                Add
              </button>
            </div>
            {serialError ? <p className="eld-error">{serialError}</p> : null}

            {serials.length > 0 ? (
              <div className="eld-batch-list">
                {serials.map((item) => (
                  <div key={item.serial} className="eld-batch-item">
                    <span className="eld-batch-item__serial">{item.serial}</span>
                    <span className={`eld-batch-item__where${item.descriptions ? '' : ' eld-batch-item__where--empty'}`}>
                      {item.descriptions || 'No location'}
                    </span>
                    <button
                      type="button"
                      className="eld-batch-item__remove"
                      onClick={() => removeSerial(item.serial)}
                      aria-label={`Remove ${item.serial}`}
                    >
                      &#x2715;
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <>
            <div className={`${p}-drawer__field`}>
              <label className={`${p}-drawer__label`} htmlFor="adv-serial">
                Serial number <span className={`${p}-drawer__required`}>*</span>
              </label>
              <input
                id="adv-serial"
                type="text"
                inputMode={picked.manufacture_id === INOVONICS_ID ? 'numeric' : 'text'}
                className={`${p}-drawer__input ${serialError ? 'is-invalid' : ''}`}
                value={serial}
                onChange={(e) => { setSerial(e.target.value); setSerialError(''); setError(''); }}
                onBlur={() => setSerialError(serial ? validateSerial(serial, picked) : '')}
                placeholder="Printed on the device label"
                autoFocus
              />
              {serialError ? <p className="eld-error">{serialError}</p> : null}
            </div>

            {/* Area devices are identified by where they sit rather than by a
                person — this is what tells staff which of three sensors in a
                suite just fired. */}
            {isAreaBase ? (
              <div className={`${p}-drawer__field`}>
                <label className={`${p}-drawer__label`} htmlFor="adv-desc">Location within area</label>
                <input
                  id="adv-desc"
                  type="text"
                  className={`${p}-drawer__input`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Bedroom, Bathroom (optional)"
                />
              </div>
            ) : null}
          </>
        )}

        {listenable && !multiMode ? (
          <div className={`${p}-drawer__field`}>
            {!listening ? (
              <button type="button" className={`${p}-btn ${p}-btn--secondary`} onClick={startListen}>
                <i className="fe fe-radio" aria-hidden="true" style={{ marginRight: 6 }} />
                Listen for device
              </button>
            ) : (
              <>
                <div className="eld-listen-bar">
                  <div
                    className="eld-listen-bar__fill"
                    style={{ width: `${(listenCount / LISTEN_SECONDS) * 100}%` }}
                  />
                </div>
                <div className="eld-listen-status">
                  <p className="eld-listen-status__text">
                    {heard.length > 0
                      ? `${heard.length} device${heard.length !== 1 ? 's' : ''} heard — ${LISTEN_SECONDS - listenCount}s left`
                      : `Press the button on the device… ${LISTEN_SECONDS - listenCount}s left`}
                  </p>
                  <button type="button" className="eld-link-btn" onClick={() => setListening(false)}>
                    Stop listening
                  </button>
                </div>
              </>
            )}

            {heard.length > 0 ? (
              <div className="eld-list" style={{ marginTop: 'var(--space-2)' }}>
                {heard.map((h) => (
                  <button
                    key={h.trx_id}
                    type="button"
                    className="eld-row"
                    onClick={() => { setSerial(String(h.trx_id)); setSerialError(''); setListening(false); }}
                  >
                    <span className="eld-row__name">{h.name || 'Device'}</span>
                    <span className="eld-row__meta">#{h.trx_id}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {/* A serial already in the system is an opportunity, not an error: the
            device exists, so offer to move it rather than making the user go
            find it (plan §7). Warning treatment only when confirming would take
            it away from whoever currently holds it. */}
        {duplicate ? (
          <div className={`eld-dup ${canReassign && dupWhere.kind !== 'none' ? 'eld-dup--warning' : ''}`}>
            <p className="eld-dup__title">
              {dupOnTarget ? 'Already assigned here' : 'Already in the system'}
            </p>

            <p className="eld-hint">
              Serial {normalizeSerial(serial, picked)} belongs to{' '}
              <strong>{duplicate.device_name || 'an existing device'}</strong>
              {dupWhere.kind === 'resident' ? <>, assigned to <strong>{dupWhere.label}</strong></> : null}
              {dupWhere.kind === 'area' ? <>, assigned to <strong>{dupWhere.label}</strong></> : null}
              {dupWhere.kind === 'none' ? ', currently unassigned' : null}.
            </p>

            {/* The mistyped-serial guard. Without naming what the device
                actually is, confirming would silently move something the user
                never intended to touch. */}
            {!dupTypeMatches ? (
              <p className="eld-hint">
                Note that is <strong>not</strong> a {picked.device_type_name} — check the serial
                if you did not mean to move it.
              </p>
            ) : null}

            {dupOnTarget ? (
              <p className="eld-hint">
                Nothing to do — it is already on {targetRow
                  ? (isAreaBase ? targetRow.area_name : (targetRow.resident_name || 'this resident'))
                  : 'this target'}.
              </p>
            ) : !assigning ? (
              <p className="eld-hint">
                Uncheck &ldquo;Leave unassigned&rdquo; and choose {isAreaBase ? 'an area' : 'a resident'} to
                move it, or close and edit the device directly.
              </p>
            ) : (
              <button
                type="button"
                className={`${p}-btn ${p}-btn--secondary`}
                onClick={handleReassign}
                disabled={saving}
                style={{ marginTop: 'var(--space-2)' }}
              >
                {saving
                  ? 'Reassigning…'
                  : dupWhere.kind === 'none' ? 'Confirm and Assign' : 'Confirm and Reassign'}
              </button>
            )}
          </div>
        ) : null}
      </div>

      {batchFailures.length > 0 ? (
        <div className="eld-error">
          {batchFailures.length} serial{batchFailures.length === 1 ? '' : 's'} could not be added — they are still listed above.
        </div>
      ) : null}

      {error ? <p className="eld-error">{error}</p> : null}
    </>
  );

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      classPrefix={p}
      ariaLabel={title}
      title={title}
      rawBody
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div className={`${p}-drawer__body`}>
          {/* Base decides which catalog is shown AND the saved is_area_device.
              Changing it invalidates the pick, so start over. */}
          <div className={`${p}-drawer__field`}>
            <span className={`${p}-drawer__label`}>Device base</span>
            <div className={`${p}-toggle-group ${p}-toggle-group--fill`}>
              {['area', 'resident'].map((b) => (
                <button
                  key={b}
                  type="button"
                  className={`${p}-toggle-btn ${base === b ? 'is-active' : ''}`}
                  onClick={() => {
                    if (base === b) return;
                    setBase(b);
                    setMultiMode(false);
                    setSearch('');
                    /* The target list is base-specific, so a resident id must
                       never survive a switch to Area. */
                    setTargetMode('assign');
                    setTargetId('');
                    resetPick();
                  }}
                >
                  {b === 'area' ? 'Area Device' : 'Resident Device'}
                </button>
              ))}
            </div>
          </div>

          {step === 'device' || !picked ? renderPickStep() : renderConfigureStep()}
        </div>

        <div className={`${p}-drawer__footer`}>
          <button type="button" className={`${p}-btn ${p}-btn--cancel`} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {step === 'configure' && picked ? (
            <button type="submit" className={`${p}-btn ${p}-btn--primary`} disabled={!canSubmit}>
              {saving
                ? (batchProgress.total
                  ? `Adding ${batchProgress.current} of ${batchProgress.total}…`
                  : 'Adding…')
                : multiMode
                  ? `Add ${serials.length || ''} Device${serials.length === 1 ? '' : 's'}`.replace('  ', ' ')
                  : assigning ? 'Add & Assign' : 'Add Unassigned'}
            </button>
          ) : null}
        </div>
      </form>
    </Drawer>
  );
};

export default AdvancedLearnInDrawer;
